/** @jsxImportSource @emotion/react */

import { yupResolver } from "@hookform/resolvers/yup";
import { Button, ConfigProvider, Skeleton } from "antd";
import { TransferApi } from "api/transfer";
import { WalletTransferPayload } from "api/transfer/type";
import { ReactComponent as TransferIcon } from "assets/Common/icons/icon-transfer.svg";
import InputCustom from "components/Common/InputCustom";
import { ResponseCode } from "contants/response";
import { useAppSelector } from "hooks/app-hook";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { selectBalance, selectBalanceLoading } from "store/user";
import { getMemberBalance } from "store/user/user.thunk";
import { theme } from "styles/theme";
import { AppDispatch, StatusNotification } from "types";
import * as yup from "yup";

import { TransferPageStyle } from "./style";

interface Props {
  username: string;
  openNotification: (status: StatusNotification, message: string) => void;
}

export const TransferWalletCard = (props: Props) => {
  const { username, openNotification } = props;
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation(["transferPage", "button", "validate"]);
  const [toggleTransfer, setToggleTranser] = useState<boolean>(true);
  const balance = useAppSelector(selectBalance);
  const isLoadingBalance = useAppSelector(selectBalanceLoading);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const schema = yup.object({
    username: yup.string().required(t("validate:FieldRequired")),
    from_wallet: yup.number().required(t("validate:FieldRequired")),
    to_wallet: yup.number().required(t("validate:FieldRequired")),
    amount: yup
      .number()
      .transform((value) => (Number.isNaN(value) ? null : value))
      .required(t("validate:FieldRequired")),
  });

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<WalletTransferPayload>({
    mode: "all",
    resolver: yupResolver(schema),
    defaultValues: {
      username,
      amount: 0,
    },
  });

  const renderShowBalance = (key: boolean) => {
    switch (key) {
      case true:
        return (
          <div className="balance">
            <span>BDT:</span>
            {isLoadingBalance ? (
              <Skeleton
                active
                style={{ height: "15px", width: "50px" }}
                paragraph={false}
              />
            ) : (
              <span className="value">{balance.total || "0.00"}</span>
            )}
          </div>
        );
      case false:
        return (
          <div className="balance">
            <span>{t("Coins")}:</span>
            {isLoadingBalance ? (
              <Skeleton
                active
                style={{ height: "13px", width: "50px" }}
                paragraph={false}
              />
            ) : (
              <span className="value">{balance.coins || "0.00"}</span>
            )}
          </div>
        );
      default:
        return null;
    }
  };

  useEffect(() => {
    if (username) {
      reset({
        username,
        amount: 0,
        from_wallet: toggleTransfer ? 1 : 2,
        to_wallet: toggleTransfer ? 2 : 1,
      });
    }
  }, [reset, toggleTransfer, username]);

  const onSubmit = async (data: WalletTransferPayload) => {
    try {
      setIsLoading(true);
      const res = await TransferApi.walletTransfer(username, {
        ...data,
        from_wallet: toggleTransfer ? 1 : 2,
        to_wallet: toggleTransfer ? 2 : 1,
      });
      setIsLoading(false);
      if (res.status === ResponseCode.SUCCESS) {
        openNotification("success", res.msg);
        dispatch(getMemberBalance({ username }));
        setValue("amount", 0);
      } else {
        openNotification("error", res.msg);
      }
    } catch (error) {
      setIsLoading(false);
      openNotification("error", error as string);
    }
  };

  return (
    <div css={TransferPageStyle.wrapInner}>
      <h3 className="title-heading">
        {t("TransferTitle", {
          left: toggleTransfer ? t("Cash") : t("Coins"),
          right: toggleTransfer ? t("Coins") : t("Cash"),
        })}
      </h3>
      <form css={TransferPageStyle.form} onSubmit={handleSubmit(onSubmit)}>
        <div className="box-transfer">
          <div className="box-item box-left">
            <h3 className="label">{toggleTransfer ? t("Cash") : t("Coins")}</h3>
            <InputCustom
              control={control}
              name="amount"
              type="number"
              placeholder={t("EnterAmount")}
              min={0}
              errors={errors.amount?.message}
              validate_status={errors.amount && "error"}
              margin_bottom="0"
            />
            {renderShowBalance(toggleTransfer)}
          </div>
          <div
            className="btn-switch"
            onClick={() => setToggleTranser((prev) => !prev)}
          >
            <TransferIcon />
          </div>
          <div className="box-item box-right">
            <h3 className="label">{toggleTransfer ? t("Coins") : t("Cash")}</h3>
            <InputCustom
              value={(() => {
                const amount = parseFloat(watch("amount").toString()) || 0;
                if (amount === 0) return "";

                if (toggleTransfer) {
                  return (amount * 1.4).toFixed(2);
                }

                return ((amount / 1.4) * 0.9).toFixed(2);
              })()}
              type="number"
              min={0}
              disabled
              placeholder={t("Converted")}
            />
            {renderShowBalance(!toggleTransfer)}
          </div>
        </div>
        <div css={TransferPageStyle.btnSubmit}>
          <ConfigProvider
            theme={{
              token: {
                colorBgContainerDisabled: theme.colors.gray[150],
                colorTextDisabled: theme.colors.white,
              },
            }}
          >
            <Button htmlType="submit" loading={isLoading}>
              {t("button:Submit")}
            </Button>
          </ConfigProvider>
        </div>
        <p className="note">{t("Notes")}</p>
      </form>
    </div>
  );
};
